const MAX_DISTANCE_DEGREES = 16.0

/**
 * Harmonizes a design hue **toward** a source hue with **matching** prevention, while keeping design hue distinct..
 *
 * Algorithm:
 * 1. If distance <= 20°: rotate **away** to achieve exactly 20° distance.
 * 2. If distance > 20°: rotate **toward** by 50% of distance (max 20°)
 *
 * @param designHue - Hue to harmonize (0-360)
 * @param sourceHue - Target hue to harmonize toward (0-360)
 * @returns Harmonized hue value (0-360)
 */
export function harmonizeHue(designHue: number, sourceHue: number) {
  const distance = calculateHueDifference(designHue, sourceHue)

  // oxlint-disable-next-line eslint/no-warning-comments -- TODO preserved per repo policy
  // TODO: disable this for minimal mode (because the primary is black/white).
  // If source is too close, rotate away from source
  if (distance <= MAX_DISTANCE_DEGREES) {
    const rotationAmount = MAX_DISTANCE_DEGREES - distance
    const rotationDirection = -getRotationDirection(designHue, sourceHue)

    return sanitizeDegrees(designHue + rotationAmount * rotationDirection)
  }

  /*
  When the sourceHue is in (2 * MAX_DISTANCE_DEGREES; MAX_DISTANCE_DEGREES) distance from the designHue,
  we shift the designHue towards the sourceHue 2x less the distance between them.
  */
  const normalRotation = Math.min(distance * 0.5, MAX_DISTANCE_DEGREES)
  const rotationDirection = getRotationDirection(designHue, sourceHue)

  return sanitizeDegrees(designHue + normalRotation * rotationDirection)
}

/**
 * Calculates the **shortest distance** (0-180) between 2 hues on the 360° color wheel.
 */
export const calculateHueDifference = (hue1: number, hue2: number) =>
  180.0 - Math.abs(Math.abs(hue1 - hue2) - 180.0)

/**
 * Determines the **direction** to rotate from one hue to another (shortest path).
 *
 * @returns 1.0 for **clockwise** rotation, -1.0 for **counterclockwise**
 */
export function getRotationDirection(fromHue: number, toHue: number) {
  const increasingDifference = sanitizeDegrees(toHue - fromHue)
  return increasingDifference <= 180.0 ? 1.0 : -1.0
}

/**
 * Normalizes degrees to the range [0, 360) because degrees can be **negative** or **exceed** 360.
 */
export function sanitizeDegrees(degrees: number) {
  degrees = degrees % 360.0
  if (degrees < 0) degrees = degrees + 360.0

  return degrees
}
